/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationTriggerMetadata} from '@angular/animations';
import {ɵAnimationEngine as AnimationEngine} from '@angular/animations/browser';
import {Injectable, NgZone, Renderer2, RendererFactory2, RendererStyleFlags2, RendererType2} from '@angular/core';
export class AnimationRendererFactory implements RendererFactory2 {
/**
 * @param {?} delegate
 * @param {?} _engine
 * @param {?} _zone
 */
constructor(
private delegate: RendererFactory2,
private _engine: AnimationEngine,
private _zone: NgZone) {
  }
/**
 * @param {?} hostElement
 * @param {?} type
 * @return {?}
 */
createRenderer(hostElement: any, type: RendererType2): Renderer2 {
    let /** @type {?} */ delegate = this.delegate.createRenderer(hostElement, type);
    if (!hostElement || !type || !type.data || !type.data['animation']) return delegate;

    const /** @type {?} */ namespaceId = type.id;
    const /** @type {?} */ animationTriggers = /** @type {?} */(( type.data['animation'] as AnimationTriggerMetadata[]));
    animationTriggers.forEach(
        trigger => this._engine.registerTrigger(trigger, namespaceify(namespaceId, trigger.name)));
    return new AnimationRenderer(delegate, this._engine, this._zone, namespaceId);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: RendererFactory2, },
{type: AnimationEngine, },
{type: NgZone, },
];
}

function AnimationRendererFactory_tsickle_Closure_declarations() {
/** @type {?} */
AnimationRendererFactory.decorators;
/**
 * @nocollapse
 * @type {?}
 */
AnimationRendererFactory.ctorParameters;
/** @type {?} */
AnimationRendererFactory.prototype.delegate;
/** @type {?} */
AnimationRendererFactory.prototype._engine;
/** @type {?} */
AnimationRendererFactory.prototype._zone;
}

export class AnimationRenderer implements Renderer2 {
public destroyNode: ((node: any) => any)|null = null;
private _flushPromise: Promise<any>|null = null;
/**
 * @param {?} delegate
 * @param {?} _engine
 * @param {?} _zone
 * @param {?} _namespaceId
 */
constructor(
public delegate: Renderer2,
private _engine: AnimationEngine,
private _zone: NgZone,
private _namespaceId: string) {
    this.destroyNode = this.delegate.destroyNode ? (n) => delegate.destroyNode !(n) : null;
  }
/**
 * @return {?}
 */
get data() { return this.delegate.data; }
/**
 * @return {?}
 */
destroy(): void { this.delegate.destroy(); }
/**
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
createElement(name: string, namespace?: string): any {
    return this.delegate.createElement(name, namespace);
  }
/**
 * @param {?} value
 * @return {?}
 */
createComment(value: string): any { return this.delegate.createComment(value); }
/**
 * @param {?} value
 * @return {?}
 */
createText(value: string): any { return this.delegate.createText(value); }
/**
 * @param {?} selectorOrNode
 * @return {?}
 */
selectRootElement(selectorOrNode: string|any): any {
    return this.delegate.selectRootElement(selectorOrNode);
  }
/**
 * @param {?} node
 * @return {?}
 */
parentNode(node: any): any { return this.delegate.parentNode(node); }
/**
 * @param {?} node
 * @return {?}
 */
nextSibling(node: any): any { return this.delegate.nextSibling(node); }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @param {?=} namespace
 * @return {?}
 */
setAttribute(el: any, name: string, value: string, namespace?: string): void {
    this.delegate.setAttribute(el, name, value, namespace);
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
removeAttribute(el: any, name: string, namespace?: string): void {
    this.delegate.removeAttribute(el, name, namespace);
  }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
addClass(el: any, name: string): void { this.delegate.addClass(el, name); }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
removeClass(el: any, name: string): void { this.delegate.removeClass(el, name); }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} value
 * @param {?} flags
 * @return {?}
 */
setStyle(el: any, style: string, value: any, flags: RendererStyleFlags2): void {
    this.delegate.setStyle(el, style, value, flags);
  }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} flags
 * @return {?}
 */
removeStyle(el: any, style: string, flags: RendererStyleFlags2): void {
    this.delegate.removeStyle(el, style, flags);
  }
/**
 * @param {?} node
 * @param {?} value
 * @return {?}
 */
setValue(node: any, value: string): void { this.delegate.setValue(node, value); }
/**
 * @param {?} parent
 * @param {?} newChild
 * @return {?}
 */
appendChild(parent: any, newChild: any): void {
    this._engine.onInsert(newChild, () => this.delegate.appendChild(parent, newChild));
    this._queueFlush();
  }
/**
 * @param {?} parent
 * @param {?} newChild
 * @param {?} refChild
 * @return {?}
 */
insertBefore(parent: any, newChild: any, refChild: any): void {
    this._engine.onInsert(newChild, () => this.delegate.insertBefore(parent, newChild, refChild));
    this._queueFlush();
  }
/**
 * @param {?} parent
 * @param {?} oldChild
 * @return {?}
 */
removeChild(parent: any, oldChild: any): void {
    this._engine.onRemove(oldChild, () => {
      // Note: if an component element has a leave animation, and the component
      // a host leave animation, the view engine will call `removeChild` for the parent
      // component renderer as well as for the child component renderer.
      // Therefore, we need to check if we already removed the element.
      if (this.delegate.parentNode(oldChild)) {
        this.delegate.removeChild(parent, oldChild);
      }
    });
    this._queueFlush();
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: any, name: string, value: any): void {
    if (name.charAt(0) == '@') {
      this._engine.setProperty(el, namespaceify(this._namespaceId, name.substr(1)), value);
      this._queueFlush();
    } else {
      this.delegate.setProperty(el, name, value);
    }
  }
/**
 * @param {?} target
 * @param {?} eventName
 * @param {?} callback
 * @return {?}
 */
listen(target: 'window'|'document'|'body'|any, eventName: string, callback: (event: any) => any):
      () => void {
    if (eventName.charAt(0) == '@') {
      const /** @type {?} */ element = resolveElementFromTarget(target);
      const [name, phase] = parseTriggerCallbackName(eventName.substr(1));
      return this._engine.listen(
          element, namespaceify(this._namespaceId, name), phase, (event: any) => {
            const /** @type {?} */ e = /** @type {?} */(( event as any));
            if (e.triggerName) {
              e.triggerName = deNamespaceify(this._namespaceId, e.triggerName);
            }
            this._zone.run(() => callback(event));
          });
    }
    return this.delegate.listen(target, eventName, callback);
  }
/**
 * @return {?}
 */
private _queueFlush() {
    if (!this._flushPromise) {
      this._zone.runOutsideAngular(() => {
        this._flushPromise = Promise.resolve(null).then(() => {
          this._flushPromise = /** @type {?} */(( null));
          this._engine.flush();
        });
      });
    }
  }
}

function AnimationRenderer_tsickle_Closure_declarations() {
/** @type {?} */
AnimationRenderer.prototype.destroyNode;
/** @type {?} */
AnimationRenderer.prototype._flushPromise;
/** @type {?} */
AnimationRenderer.prototype.delegate;
/** @type {?} */
AnimationRenderer.prototype._engine;
/** @type {?} */
AnimationRenderer.prototype._zone;
/** @type {?} */
AnimationRenderer.prototype._namespaceId;
}

/**
 * @param {?} target
 * @return {?}
 */
function resolveElementFromTarget(target: 'window' | 'document' | 'body' | any): any {
  switch (target) {
    case 'body':
      return document.body;
    case 'document':
      return document;
    case 'window':
      return window;
    default:
      return target;
  }
}
/**
 * @param {?} triggerName
 * @return {?}
 */
function parseTriggerCallbackName(triggerName: string) {
  const /** @type {?} */ dotIndex = triggerName.indexOf('.');
  const /** @type {?} */ trigger = triggerName.substring(0, dotIndex);
  const /** @type {?} */ phase = triggerName.substr(dotIndex + 1);
  return [trigger, phase];
}
/**
 * @param {?} namespaceId
 * @param {?} value
 * @return {?}
 */
function namespaceify(namespaceId: string, value: string): string {
  return `${namespaceId}#${value}`;
}
/**
 * @param {?} namespaceId
 * @param {?} value
 * @return {?}
 */
function deNamespaceify(namespaceId: string, value: string): string {
  return value.replace(namespaceId + '#', '');
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
